
import { useAuth as useClerkAuth, useUser } from "@clerk/clerk-react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader } from "lucide-react";
import { useEffect, useState } from "react";
import { isAuthenticated } from "@/integrations/supabase/client";

interface SecureAuthGateProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

export default function SecureAuthGate({ 
  children, 
  requireAuth = true
}: SecureAuthGateProps) {
  const { isSignedIn: clerkSignedIn, isLoaded: clerkLoaded } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const location = useLocation();
  const [authChecked, setAuthChecked] = useState(false);

  // Perform comprehensive authentication check
  useEffect(() => {
    if (clerkLoaded) {
      const hasValidAuth = clerkSignedIn && clerkUser && isAuthenticated();
      
      if (requireAuth && !hasValidAuth) {
        console.log('SecureAuthGate: Authentication required but user not properly authenticated');
      }
      
      setAuthChecked(true);
    }
  }, [clerkLoaded, clerkSignedIn, clerkUser, requireAuth]);

  // Loading state while authentication is being verified
  if (!clerkLoaded || !authChecked) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  // Check if authentication is required and valid
  const needsAuth = requireAuth && (
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/profile") ||
    location.pathname === "/clerk-demo"
  );

  const hasValidAuth = clerkSignedIn && clerkUser && isAuthenticated();

  if (needsAuth && !hasValidAuth) {
    // Store current path for redirect after login
    sessionStorage.setItem('redirect_after_login', location.pathname);
    
    console.log('SecureAuthGate: Redirecting unauthenticated user to login');
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
